import {
  Box,
  Card,
  FormControl,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useTranslate } from 'react-admin';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { customPalette } from '../../themes/customTheme';

export const GraphicCard = ({ data, dateFrom, dateTo }) => {
  const t = useTranslate();
  const [variable, setVariable] = useState('total_visits');

  const handleChange = event => {
    setVariable(event.target.value);
  };

  const getLabel = variable => {
    let parts = variable.split('_');
    return parts[1];
  };

  const formatTooltipLabel = label => `Día ${label}`;
  const formatTooltipValue = (value, name) => {
    let formattedValue = Intl.NumberFormat('es-AR', {
      maximumFractionDigits: 2,
    }).format(value);
    let part = getLabel(name);
    return [formattedValue, t(`resources.reports.fields.${part}`)];
  };

  const numberFormatter = value => {
    return new Intl.NumberFormat('es-AR', {
      maximumFractionDigits: 2,
    }).format(value);
  };

  const fillEmptyDays = data => {
    function getDaysBetweenDates(dateFrom, dateTo) {
      const oneDay = 24 * 60 * 60 * 1000;
      const startDate = new Date(dateFrom);
      const endDate = new Date(dateTo);
      return Math.ceil((endDate - startDate) / oneDay) + 1;
    }

    function formatDate(date) {
      return `${date.getUTCDate().toString().padStart(2, '0')}/${(date.getUTCMonth() + 1).toString().padStart(2, '0')}`;
    }

    const completeData = Array.from(
      { length: getDaysBetweenDates(dateFrom, dateTo) },
      (_, index) => {
        const currentDate = new Date(
          Date.UTC(
            new Date(dateFrom).getUTCFullYear(),
            new Date(dateFrom).getUTCMonth(),
            new Date(dateFrom).getUTCDate() + index
          )
        );
        const foundItem = data.find(item => {
          const itemDate = new Date(
            Date.UTC(
              new Date(item.view_date).getUTCFullYear(),
              new Date(item.view_date).getUTCMonth(),
              new Date(item.view_date).getUTCDate()
            )
          );

          return formatDate(itemDate) === formatDate(currentDate);
        });

        return foundItem
          ? {
              date: formatDate(currentDate),
              total_visits: Number(foundItem.total_visits) || 0,
              total_subscribers: Number(foundItem.total_subscribers) || 0,
              total_notes: Number(foundItem.total_notes) || 0,
              total_rfv: Number(foundItem.total_rfv) || 0,
            }
          : {
              date: formatDate(currentDate),
              total_visits: null,
              total_subscribers: null,
              total_notes: null,
              total_rfv: null,
            };
      }
    );

    return completeData;
  };

  const graphic_data = fillEmptyDays(data);

  if (!graphic_data) return null;
  return (
    <Card
      elevation={0}
      sx={{
        background: '#fff',
        color: '#000',
        padding: '24px',
        marginTop: 2,
        marginBottom: '1em',
        minHeight: '400px',
        minWidth: '500px',
      }}
    >
      <Typography fontSize={26} fontWeight="600" sx={{ marginBottom: '26px' }}>
        {t('resources.reports.list.graphic_title')}
      </Typography>

      <Box
        flexDirection={{ xs: 'column', lg: 'row' }}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          minHeight: '300px',
        }}
      >
        <Box sx={{ width: '100%', height: 'auto ' }}>
          <ResponsiveContainer width="100%" height={340}>
            <LineChart
              width={600}
              height={240}
              data={graphic_data}
              margin={{
                top: 10,
                right: 30,
                left: 10,
                bottom: 0,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis tickFormatter={numberFormatter} />
              <Tooltip
                labelFormatter={formatTooltipLabel}
                formatter={formatTooltipValue}
              />
              <Line
                connectNulls
                type="monotone"
                dataKey={variable}
                stroke={customPalette[getLabel(variable)].main}
                fill={customPalette[getLabel(variable)].main}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
        <Box>
          <FormControl
            sx={{ m: 1, minWidth: 160, borderRadius: '10px!important' }}
          >
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={variable}
              onChange={handleChange}
            >
              <MenuItem value="total_visits">
                {t('resources.reports.fields.visits')}
              </MenuItem>
              <MenuItem value="total_notes">
                {t('resources.reports.fields.notes')}
              </MenuItem>
              <MenuItem value="total_subscribers">
                {t('resources.reports.fields.subscribers')}
              </MenuItem>
              <MenuItem value="total_rfv">
                {t('resources.reports.fields.rfv')}
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>
    </Card>
  );
};
